import React, { useState, useEffect, useCallback } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { ArrowLeftIcon, ClockIcon, UserGroupIcon, AcademicCapIcon, FireIcon, TrophyIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { SudokuIcon, MystereIcon, BrinIcon } from '../../components/games/GameIcons';
import { useAuth } from '../../hooks/useAuth';

const MONTH_NAMES_FR = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];

const GAME_BREAKDOWN = [
  { key: 'brinScore',    label: 'Brin',    emoji: '🟢' },
  { key: 'sudokuScore',  label: 'Sudoku',  emoji: '🟣' },
  { key: 'mystereScore', label: 'Mystère', emoji: '🔵' },
];

function UserBreakdownModal({ userData, onClose }) {
  const games = GAME_BREAKDOWN
    .map(g => ({ ...g, pts: userData[g.key] || 0 }))
    .sort((a, b) => b.pts - a.pts);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={onClose}>
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-lg text-gray-900">{userData.username}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl leading-none">✕</button>
        </div>
        <div className="text-center mb-5">
          <div className="text-3xl font-black text-accent">{(userData.lifetimeScore || 0).toLocaleString()}</div>
          <div className="text-xs text-gray-500 font-medium uppercase tracking-wide">Points total</div>
        </div>
        <div className="space-y-2">
          {games.map(g => (
            <div key={g.key} className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-2.5">
              <span className="text-sm font-medium text-gray-700">{g.emoji} {g.label}</span>
              <span className="font-bold text-gray-900">{g.pts.toLocaleString()} pts</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function GlobalLeaderboard() {
  const { user } = useAuth();
  const router = useRouter();
  const [tab, setTab] = useState('alltime');
  const [allTimeData, setAllTimeData] = useState(null);
  const [monthlyData, setMonthlyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [fetchingUser, setFetchingUser] = useState(false);
  const api = process.env.NEXT_PUBLIC_API_URL;

  const fetchData = useCallback(() => {
    setLoading(true);
    if (!api) { setLoading(false); return; }
    const headers = {};
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    if (token) headers['Authorization'] = `Bearer ${token}`;
    Promise.all([
      fetch(`${api}/games/leaderboard`, { headers, credentials: 'include' }).then(r => r.json()).catch(() => null),
      fetch(`${api}/games/leaderboard/monthly`, { headers, credentials: 'include' }).then(r => r.json()).catch(() => null),
    ]).then(([all, monthly]) => {
      if (all?.success && all.top25?.length > 0) setAllTimeData(all);
      if (monthly?.success && monthly.top25?.length > 0) setMonthlyData(monthly);
    }).finally(() => setLoading(false));
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    window.addEventListener('leaderboard-updated', fetchData);
    return () => window.removeEventListener('leaderboard-updated', fetchData);
  }, [fetchData]);

  useEffect(() => {
    const onRouteChange = (url) => { if (url === '/jeux' || url.startsWith('/jeux?')) fetchData(); };
    router.events.on('routeChangeComplete', onRouteChange);
    return () => router.events.off('routeChangeComplete', onRouteChange);
  }, [router.events, fetchData]);

  const openUserBreakdown = useCallback(async (playerData) => {
    // If per-game fields already present, show immediately
    if (typeof playerData.brinScore !== 'undefined' || typeof playerData.sudokuScore !== 'undefined') {
      setSelectedUser(playerData);
      return;
    }
    // Otherwise fetch from API (e.g. monthly tab users or current user outside top25)
    if (!api || !playerData.userId) return;
    setFetchingUser(true);
    try {
      const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${api}/games/leaderboard/user/${playerData.userId}`, { headers, credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        if (data.success) setSelectedUser(data);
      }
    } catch { /* ignore */ } finally { setFetchingUser(false); }
  }, [api]);

  const medals = ['🥇', '🥈', '🥉'];
  const now = new Date();
  const monthLabel = `${MONTH_NAMES_FR[now.getMonth()]} ${now.getFullYear()}`;
  const activeData = tab === 'monthly' ? monthlyData : allTimeData;
  const scoreKey = tab === 'monthly' ? 'monthlyScore' : 'lifetimeScore';
  const scoreLabel = tab === 'monthly' ? 'Score du mois' : 'Score total';

  return (
    <div className="mt-12 bg-theme-secondary rounded-2xl border border-theme overflow-hidden">
      {selectedUser && <UserBreakdownModal userData={selectedUser} onClose={() => setSelectedUser(null)} />}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 px-6 py-4 border-b border-theme">
        <div className="flex items-center gap-3">
          <TrophyIcon className="w-5 h-5 text-yellow-500" />
          <h2 className="text-lg font-bold text-theme-primary">Classement global</h2>
        </div>
        <div className="flex gap-2 sm:ml-auto items-center">
          <button
            onClick={() => setTab('monthly')}
            className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${tab === 'monthly' ? 'bg-accent text-white' : 'bg-theme-primary/10 text-theme-secondary hover:bg-theme-primary/20'}`}
          >
            {monthLabel}
          </button>
          <button
            onClick={() => setTab('alltime')}
            className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${tab === 'alltime' ? 'bg-accent text-white' : 'bg-theme-primary/10 text-theme-secondary hover:bg-theme-primary/20'}`}
          >
            Tout temps
          </button>
          <button
            onClick={fetchData}
            disabled={loading}
            className="p-1.5 rounded-full text-theme-secondary hover:text-accent hover:bg-theme-primary/10 transition-colors disabled:opacity-40"
            title="Actualiser"
          >
            <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {tab === 'monthly' && (
        <div className="px-6 py-2 bg-yellow-50 border-b border-yellow-200 text-xs text-yellow-800 font-medium text-center">
          🏆 Top 5 de ce mois seront récompensés. Jouez chaque jour pour grimper !
        </div>
      )}

      <div className="px-4 py-1.5 bg-theme-primary/5 border-b border-theme text-xs text-theme-secondary text-center">
        Cliquez sur un joueur pour voir le détail par jeu
      </div>

      {loading ? (
        <div className="p-8 text-center text-theme-secondary text-sm">Chargement...</div>
      ) : !activeData || activeData.top25?.length === 0 ? (
        <div className="p-8 text-center text-theme-secondary text-sm">
          {tab === 'monthly' ? 'Aucun score ce mois-ci encore. Soyez le premier !' : 'Jouez une partie pour que vos scores apparaissent ici.'}
        </div>
      ) : (
        <div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-theme-primary/5">
                <tr>
                  <th className="text-left px-4 py-2 font-medium text-theme-secondary w-12">Rang</th>
                  <th className="text-left px-4 py-2 font-medium text-theme-secondary">Joueur</th>
                  <th className="text-right px-4 py-2 font-medium text-theme-secondary">{scoreLabel}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-theme">
                {activeData.top25.map((p, i) => {
                  const isMe = user && p.userId === (user.userId || user.id);
                  const isTop5 = i < 5 && tab === 'monthly';
                  return (
                    <tr
                      key={p.userId}
                      onClick={() => openUserBreakdown(p)}
                      className={`cursor-pointer transition-colors ${isMe ? 'bg-accent-light font-semibold' : isTop5 ? 'bg-yellow-50/50 hover:bg-yellow-100/60' : 'hover:bg-theme-primary/5'}`}
                    >
                      <td className="px-4 py-2.5 text-center">
                        {i < 3 ? <span className="text-base">{medals[i]}</span> : <span className={`font-mono ${isTop5 ? 'text-yellow-700 font-bold' : 'text-theme-secondary'}`}>{p.rank}</span>}
                      </td>
                      <td className="px-4 py-2.5 text-theme-primary">
                        <span className="hover:underline">{p.username}</span>
                        {isMe && <span className="ml-2 text-xs text-accent">(vous)</span>}
                        {isTop5 && !isMe && i >= 3 && <span className="ml-2 text-xs text-yellow-600">🏅</span>}
                      </td>
                      <td className="px-4 py-2.5 text-right font-bold text-accent">{(p[scoreKey] || 0).toLocaleString()}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {activeData.currentUser && !activeData.top25.find(p => p.userId === activeData.currentUser.userId) && (
            <div
              onClick={() => openUserBreakdown(activeData.currentUser)}
              className="border-t border-theme px-4 py-3 bg-accent-light flex items-center justify-between text-sm cursor-pointer hover:bg-accent-light/80 transition-colors"
            >
              <span className="text-theme-secondary">
                Votre classement:{' '}
                <strong className="text-accent hover:underline">
                  {activeData.currentUser.rank === '—' || !activeData.currentUser[scoreKey] ? 'Non classé' : `#${activeData.currentUser.rank}`}
                </strong>
                {' — '}<span className="hover:underline">{activeData.currentUser.username}</span>
              </span>
              <span className="font-bold text-accent">{(activeData.currentUser[scoreKey] || 0).toLocaleString()} pts</span>
            </div>
          )}
          {fetchingUser && <div className="p-2 text-center text-xs text-theme-secondary">Chargement...</div>}
        </div>
      )}
    </div>
  );
}

// Visual identity per game
const GAME_CONFIG = [
  {
    id: 'mystere',
    name: 'Mystère',
    description: 'Devinez le mot de 5 lettres en 6 tentatives.',
    Icon: MystereIcon,
    difficulty: 'Moyen',
    players: '1 joueur',
    duration: '5 min',
    category: 'Vocabulaire',
    path: '/jeux/mystere',
    color: '#1e3a5f',
    bg: '#dbeafe',
    accent: '#2563eb',
    emoji: '🔵',
    statsKey: 'mystere-stats-',
  },
  {
    id: 'brin',
    name: 'Brin',
    description: 'Remplissez la grille de couleurs sans répétition dans chaque ligne et colonne. Plus vite, plus de points !',
    Icon: BrinIcon,
    difficulty: 'Débutant → Expert',
    players: '1 joueur',
    duration: '1–5 min',
    category: 'Logique',
    path: '/jeux/brin',
    color: '#1b4332',
    bg: '#d1fae5',
    accent: '#16a34a',
    emoji: '🟢',
    statsKey: 'brin_v2_hs',
  },
  {
    id: 'sudoku',
    name: 'Sudoku',
    description: 'Remplissez la grille 9×9 sans répétition.',
    Icon: SudokuIcon,
    difficulty: 'Variable',
    players: '1 joueur',
    duration: '5–30 min',
    category: 'Logique',
    path: '/jeux/sudoku',
    color: '#581c87',
    bg: '#ede9fe',
    accent: '#7c3aed',
    emoji: '🟣',
    statsKey: 'sudoku-stats-',
  },
];

function useCountdown() {
  const [timeLeft, setTimeLeft] = useState('');
  useEffect(() => {
    const calc = () => {
      const now = new Date();
      const midnight = new Date(now);
      midnight.setHours(24, 0, 0, 0);
      const diff = midnight - now;
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setTimeLeft(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`);
    };
    calc();
    const id = setInterval(calc, 1000);
    return () => clearInterval(id);
  }, []);
  return timeLeft;
}

function getStreak(userId) {
  if (typeof window === 'undefined') return 0;
  try {
    const key = `mystere-stats-${userId || 'anonymous'}`;
    const d = JSON.parse(localStorage.getItem(key));
    return d?.currentStreak || 0;
  } catch { return 0; }
}

function hasPlayedToday(gameId, userId) {
  if (typeof window === 'undefined') return false;
  const today = new Date().toISOString().slice(0, 10);
  try {
    if (gameId === 'mystere') {
      const state = JSON.parse(localStorage.getItem(`mystere-unfinished-${userId || 'anonymous'}`) || 'null');
      const done = localStorage.getItem(`mystere-done-${today}-${userId || 'anonymous'}`);
      return !!(done || (state && state.gameStatus !== 'playing'));
    }
    if (gameId === 'brin') {
      const epoch = new Date('2026-05-01T00:00:00');
      const todayD = new Date(); todayD.setHours(0,0,0,0); epoch.setHours(0,0,0,0);
      const puzzleIdx = Math.floor(Math.abs(todayD - epoch) / 86400000) % 2 + 1;
      return !!localStorage.getItem(`brin_final_puzzle_${puzzleIdx}`);
    }
    return false;
  } catch { return false; }
}

const JeuxIndex = () => {
  const router = useRouter();
  const { user, isAuthenticated } = useAuth();
  const countdown = useCountdown();
  const [streak, setStreak] = useState(0);
  const [mounted, setMounted] = useState(false);
  const [playedToday, setPlayedToday] = useState({});
  const [hoveredGame, setHoveredGame] = useState(null);

  useEffect(() => {
    setMounted(true);
    const uid = user?.userId || user?.id || 'anonymous';
    setStreak(getStreak(uid));
    const played = {};
    GAME_CONFIG.forEach(g => { played[g.id] = hasPlayedToday(g.id, uid); });
    setPlayedToday(played);
  }, [user]);

  return (
    <>
      <Head>
        <title>Jeux: Mystère, Brin, Sudoku | Fenkparet</title>
        <meta name="description" content="Jouez chaque jour à Mystère (Wordle français), Brin (jeu de lettres) et Sudoku. Nouveaux défis quotidiens gratuits en français !" />
        <meta name="keywords" content="jeux en français, jeu quotidien, wordle français, jeu de lettres, sudoku, jeux de mots, défi du jour" />
        <link rel="canonical" href="https://fenkparet.com/jeux" />
        <meta property="og:title" content="Jeux — Mystère, Brin, Sudoku | Fenkparet" />
        <meta property="og:description" content="Trois jeux quotidiens gratuits en français. Nouveau défi chaque jour !" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://fenkparet.com/jeux" />
        <meta property="og:locale" content="fr_FR" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Jeux quotidiens en français | Fenkparet" />
        <meta name="twitter:description" content="Mystère, Brin, Sudoku — trois défis quotidiens gratuits." />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'Jeux quotidiens — Fenkparet',
          description: 'Trois jeux quotidiens gratuits en français : Mystère, Brin et Sudoku.',
          url: 'https://fenkparet.com/jeux',
          hasPart: [
            { '@type': 'Game', name: 'Mystère', url: 'https://fenkparet.com/jeux/mystere' },
            { '@type': 'Game', name: 'Brin', url: 'https://fenkparet.com/jeux/brin' },
            { '@type': 'Game', name: 'Sudoku', url: 'https://fenkparet.com/jeux/sudoku' },
          ],
        }) }} />
      </Head>

      <div className="min-h-screen bg-theme-primary">
        <div className="container mx-auto px-4 py-8 max-w-5xl">

          {/* Back button */}
          <div className="mb-6">
            <button onClick={() => router.push('/')}
              className="flex items-center gap-2 text-theme-secondary hover:text-theme-primary transition-colors">
              <ArrowLeftIcon className="w-5 h-5" />
              Retour à l&apos;accueil
            </button>
          </div>

          {/* Hero section */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-4 py-1.5 rounded-full text-sm font-semibold mb-4 animate-pop-in">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse inline-block"></span>
              Défi du jour disponible
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-theme-primary mb-3">Jeux</h1>
            <p className="text-lg text-theme-secondary mb-4">Nouveau défi chaque jour</p>

            {/* Countdown */}
            <div className="inline-flex items-center gap-2 bg-theme-secondary border border-theme rounded-xl px-5 py-2.5">
              <ClockIcon className="w-4 h-4 text-theme-secondary" />
              <span className="text-sm text-theme-secondary">Prochain puzzle dans</span>
              <span className="font-mono font-bold text-accent text-lg animate-countdown">{mounted ? countdown : '--:--:--'}</span>
            </div>

            {/* Streak */}
            {mounted && streak > 0 && (
              <div className="mt-4 inline-flex items-center gap-2 bg-yellow-50 border border-yellow-200 rounded-xl px-5 py-2.5 animate-streak-glow">
                <FireIcon className="w-5 h-5 text-orange-500" />
                <span className="font-bold text-orange-600">Vous avez joué {streak} jour{streak > 1 ? 's' : ''} d&apos;affilée !</span>
                {streak >= 7 && <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-semibold">🏆 Série {streak}</span>}
              </div>
            )}
          </div>

          {/* Game cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {GAME_CONFIG.map((game) => {
              const done = playedToday[game.id];
              const isHovered = hoveredGame === game.id;
              return (
                <div
                  key={game.id}
                  onMouseEnter={() => setHoveredGame(game.id)}
                  onMouseLeave={() => setHoveredGame(null)}
                  style={{
                    borderTop: `4px solid ${game.accent}`,
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    transform: isHovered ? 'translateY(-6px)' : 'translateY(0)',
                    boxShadow: isHovered ? `0 12px 32px ${game.accent}22` : '0 2px 8px rgba(0,0,0,0.08)',
                  }}
                  className="bg-theme-secondary rounded-xl border border-theme relative overflow-hidden"
                >
                  {/* Done badge */}
                  {mounted && done && (
                    <div className="absolute top-3 right-3 bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                      ✓ Joué
                    </div>
                  )}

                  <div className="p-6">
                    {/* Icon */}
                    <div className="text-center mb-4">
                      <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-3"
                        style={{ backgroundColor: game.bg }}>
                        <game.Icon size={32} color={game.accent} />
                      </div>
                      <h2 className="text-2xl font-bold text-theme-primary">{game.name}</h2>
                    </div>

                    <p className="text-theme-secondary text-center mb-5 text-sm leading-relaxed">{game.description}</p>

                    <div className="space-y-2 mb-5">
                      <div className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-2 text-theme-secondary"><AcademicCapIcon className="w-4 h-4" /> Difficulté :</span>
                        <span className="font-medium" style={{ color: game.accent }}>{game.difficulty}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-2 text-theme-secondary"><UserGroupIcon className="w-4 h-4" /> Joueurs :</span>
                        <span className="font-medium" style={{ color: game.accent }}>{game.players}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-2 text-theme-secondary"><ClockIcon className="w-4 h-4" /> Durée :</span>
                        <span className="font-medium" style={{ color: game.accent }}>{game.duration}</span>
                      </div>
                    </div>

                    <div className="flex justify-center mb-4">
                      <span className="px-3 py-1 rounded-full text-xs font-semibold"
                        style={{ backgroundColor: game.bg, color: game.accent }}>
                        {game.category}
                      </span>
                    </div>

                    <a href={game.path}
                      className="block w-full text-white py-3 px-6 rounded-xl font-semibold text-center transition-all duration-200 hover:opacity-90 active:scale-95"
                      style={{ backgroundColor: game.accent }}>
                      {done ? `Rejouer à ${game.name}` : `Jouer à ${game.name}`}
                    </a>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Login nudge */}
          {mounted && !isAuthenticated && (
            <div className="mt-8 text-center p-4 bg-theme-secondary rounded-xl border border-theme">
              <p className="text-theme-secondary text-sm">
                <a href="/auth/login" className="text-accent font-semibold hover:underline">Connectez-vous</a>
                {' '}pour sauvegarder vos scores, suivre vos séries et apparaître au classement.
              </p>
            </div>
          )}

          {/* Global leaderboard */}
          <GlobalLeaderboard />
        </div>
      </div>
    </>
  );
};

export default JeuxIndex;
